import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listMyShopOrders } from "@/lib/shop.functions";
import { downloadFile } from "@/lib/download-file";
import { formatPrice } from "./shop.index";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";

export const Route = createFileRoute("/shop/downloads")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Your downloads — Recovery Trauma™ Shop" },
      {
        name: "description",
        content: "Everything you've bought from the Recovery Trauma™ shop, ready to download again.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: MyDownloadsPage,
});

function MyDownloadsPage() {
  const fetchOrders = useServerFn(listMyShopOrders);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["shop", "my-orders"],
    queryFn: () => fetchOrders(),
    retry: false,
  });

  const orders = data?.orders ?? [];

  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-12">
      <h1 className="text-2xl font-semibold">Your downloads</h1>
      <p className="mt-2 text-muted-foreground">
        Everything you've bought from the shop. Links refresh each time you open this page.
      </p>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" />
        </div>
      ) : isError ? (
        <p className="mt-8 rounded-xl border bg-card p-5 text-muted-foreground">
          Please{" "}
          <Link to="/login" className="text-primary underline">
            log in
          </Link>{" "}
          to see your downloads.
        </p>
      ) : orders.length === 0 ? (
        <p className="mt-8 rounded-xl border bg-card p-5 text-muted-foreground">
          You haven't bought anything yet.{" "}
          <Link to="/shop" className="text-primary underline">
            Visit the shop
          </Link>
          .
        </p>
      ) : (
        <ul className="mt-8 space-y-3">
          {orders.map((o) => (
            <li
              key={o.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card p-4"
            >
              <div>
                <p className="font-medium">{o.product_title ?? "Download"}</p>
                <p className="text-sm text-muted-foreground">
                  {new Date(o.created_at).toLocaleDateString("en-GB")} ·{" "}
                  {formatPrice(o.amount_cents, o.currency)}
                </p>
              </div>
              {o.downloadUrl ? (
                <Button
                  variant="secondary"
                  onClick={() =>
                    void downloadFile(o.downloadUrl!, o.product_title ?? undefined)
                  }
                >
                  <Download className="me-2 h-4 w-4" aria-hidden="true" /> Download
                </Button>
              ) : (
                <span className="text-sm text-muted-foreground">File coming soon</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
